import AddLocationAltOutlined from "@mui/icons-material/AddLocationAltOutlined";
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useAlerts } from "components/Alerts/AlertProvider";
import { AssociateFacilitiesDialog } from "components/AssociateFacilities/AssociateFacilitiesDialog";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import {
  ReviewerDueDateShiftDialog,
  type DueDateShiftReviewer,
} from "components/Submission/ReviewerDueDateShiftDialog";
import { ReviewerPicker } from "components/Submission/ReviewersEditList";
import { SubmissionFacilitiesDatagrid } from "components/Submission/SubmissionFacilitiesDatagrid";
import { SubmissionFormProvider } from "contexts/SubmissionFormContext";
import { RecipientType } from "encamp-shared/src/constants/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SubmissionMethod,
  SubmissionReasonForSubmittal,
  SubmissionReviewerInput,
} from "generated-graphql/graphql";
import { useUpdateSubmission } from "hooks/useSubmissionMutations";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { parseDateLikeToLocalDateTime, toUtcMidnightIsoFromDateLike } from "util/dates";

import { isSubmissionMethod } from "../submissionEnumGuards";
import { isReasonForSubmittal, reasonForSubmittalOptionsForSite } from "./reasonForSubmittalLabels";

export type SubmissionDetailsAssignee = {
  id: string;
  name: string;
  email: string;
} | null;

/**
 * When present and the due date changes on save, the dialog offers to shift
 * reviewer due dates by the same delta and persists them in the same save.
 */
export type DueDateShiftConfig = {
  reviewers: DueDateShiftReviewer[];
  onConfirm: (deltaDays: number) => void;
  buildReviewerInputs: (deltaDays: number) => SubmissionReviewerInput[];
};

type Props = {
  submissionId: string;
  /** Encamp Assignee is an internal staff assignment — only editable by staff. */
  isStaff: boolean;
  /** §1 Reason for Submittal and §18 Public Comments are 8700-12-only fields. */
  isRcra870012: boolean;
  /**
   * The facility has no active EPA ID, so this 8700-12 registers a *new* site.
   * Restricts §1 Reason for Submittal to the two "obtaining an ID" reasons (no
   * discontinuation) and surfaces a notice that a new EPA ID will be requested.
   * Defaults to false (an existing site) when EPA ID presence isn't known.
   */
  isNewSite?: boolean;
  /**
   * Document forms edit their user-chosen title here; the derived 8700-12
   * title stays read-only (the default).
   */
  allowTitleEdit?: boolean;
  initialTitle?: string;
  initialDueDate: string | null;
  initialAssignee: SubmissionDetailsAssignee;
  initialNotes: string;
  initialReasonForSubmittal: SubmissionReasonForSubmittal | "";
  /** Ignored for `isRcra870012` submissions regardless of the values passed in. */
  initialRecipient?: string;
  initialSubmissionMethod?: SubmissionMethod | "";
  initialFacilities?: Array<{ id: string; name: string }>;
  /**
   * Requirement-created (SYSTEM) submissions can't have their facilities
   * changed — the update mutation ignores facility edits for them — so the
   * add/remove controls are hidden rather than offering an edit that a save
   * would silently drop.
   */
  canEditFacilities?: boolean;
  dueDateShift?: DueDateShiftConfig;
  onClose: () => void;
};

function DocumentFormFieldsSection({
  facilityIds,
  canEditFacilities,
  onChangeFacility,
  onRemoveFacility,
  recipient,
  onRecipientChange,
  submissionMethod,
  onSubmissionMethodChange,
  rowSx,
}: {
  facilityIds: string[];
  canEditFacilities: boolean;
  onChangeFacility: () => void;
  onRemoveFacility: (facilityId: string) => void;
  recipient: string;
  onRecipientChange: (next: string) => void;
  submissionMethod: SubmissionMethod | "";
  onSubmissionMethodChange: (next: SubmissionMethod) => void;
  rowSx: SxProps<Theme>;
}) {
  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {facilityIds.length === 1 ? "Facility" : "Facilities"}
          </Typography>
          {canEditFacilities && (
            <IconButton
              size="small"
              sx={{ color: "primary.main" }}
              onClick={onChangeFacility}
              title="Add facilities"
            >
              <AddLocationAltOutlined fontSize="small" />
            </IconButton>
          )}
        </Box>
        {/* The grid reads compliance issues from the form context, which the
            steps that open this dialog don't provide; there are none to show here. */}
        <SubmissionFormProvider issues={[]}>
          <SubmissionFacilitiesDatagrid
            facilityIds={facilityIds}
            onRemoveFacility={canEditFacilities ? onRemoveFacility : undefined}
          />
        </SubmissionFormProvider>
      </Box>
      <Box sx={{ ...rowSx, mb: 2 }}>
        <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 220 }}>
          <InputLabel>Recipient</InputLabel>
          <Select
            label="Recipient"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value)}
          >
            <MenuItem value={RecipientType.REGULATORY_AGENCY}>Regulatory Agency</MenuItem>
            <MenuItem value={RecipientType.THIRD_PARTY}>Third Party</MenuItem>
            <MenuItem value={RecipientType.INTERNAL}>Internal</MenuItem>
            <MenuItem value={RecipientType.OTHER}>Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 220 }}>
          <InputLabel>Submission method</InputLabel>
          <Select
            label="Submission method"
            value={submissionMethod}
            onChange={(e) => {
              if (isSubmissionMethod(e.target.value)) {
                onSubmissionMethodChange(e.target.value);
              }
            }}
          >
            {Object.values(SubmissionMethod).map((value) => (
              <MenuItem key={value} value={value}>
                {prettyPrintEnumValue(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );
}

/**
 * Modal editor for the submission-level Details (due date, Encamp assignee,
 * the 8700-12 §1/§18 fields, and — for document forms — facility, recipient,
 * and submission method, matching what's editable on the legacy submission
 * form). Opened from the pencil on the Details card; the card itself renders
 * the values read-only. Saving persists via `updateSubmission` (which
 * refetches the stepper shell, so the card reflects the new values) and
 * closes the dialog; an unsaved-edit close is confirmed.
 *
 * The 8700-12 title is derived from facility + form and stays read-only; document
 * forms pass `allowTitleEdit` to edit their user-chosen title here.
 */
export function EditSubmissionDetailsDialog({
  submissionId,
  isStaff,
  isRcra870012,
  isNewSite = false,
  allowTitleEdit = false,
  initialTitle = "",
  initialDueDate,
  initialAssignee,
  initialNotes,
  initialReasonForSubmittal,
  initialRecipient = "",
  initialSubmissionMethod = "",
  initialFacilities = [],
  canEditFacilities = true,
  dueDateShift,
  onClose,
}: Props) {
  const alerts = useAlerts();
  const [updateSubmission, { loading: saving }] = useUpdateSubmission();

  // Due dates are stored as UTC midnight (see `toUtcMidnightIsoFromDateLike`).
  // Parse via `parseDateLikeToLocalDateTime`, which keys off the literal
  // calendar day rather than converting the UTC instant into the browser's
  // local zone — a plain `DateTime.fromISO` would roll the day back in any
  // negative-offset zone, seeding (and re-saving) the date one day early.
  const initialDate = useMemo(
    () => (initialDueDate ? (parseDateLikeToLocalDateTime(initialDueDate) ?? null) : null),
    [initialDueDate]
  );

  const [title, setTitle] = useState<string>(initialTitle);
  const [date, setDate] = useState<DateTime | null>(initialDate);
  const [assignee, setAssignee] = useState<SubmissionDetailsAssignee>(initialAssignee);
  const [notes, setNotes] = useState<string>(initialNotes);
  const [reasonForSubmittal, setReasonForSubmittal] = useState<SubmissionReasonForSubmittal | "">(
    initialReasonForSubmittal
  );
  const [recipient, setRecipient] = useState<string>(initialRecipient);
  const [submissionMethod, setSubmissionMethod] = useState<SubmissionMethod | "">(
    initialSubmissionMethod
  );
  const initialFacilityIds = useMemo(
    () => initialFacilities.map((facility) => facility.id),
    [initialFacilities]
  );
  const [facilityIds, setFacilityIds] = useState<string[]>(initialFacilityIds);
  const [changeFacilityOpen, setChangeFacilityOpen] = useState(false);

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  // Non-null while the due-date-shift confirmation is open, carrying the delta to
  // apply if the user accepts the shift.
  const [pendingShiftDeltaDays, setPendingShiftDeltaDays] = useState<number | null>(null);

  const facilitiesChanged =
    facilityIds.length !== initialFacilityIds.length ||
    facilityIds.some((id) => !initialFacilityIds.includes(id));
  const isDirty =
    (allowTitleEdit && title !== initialTitle) ||
    (date?.toISODate() ?? null) !== (initialDate?.toISODate() ?? null) ||
    (assignee?.id ?? null) !== (initialAssignee?.id ?? null) ||
    notes !== initialNotes ||
    (isRcra870012 && reasonForSubmittal !== initialReasonForSubmittal) ||
    (!isRcra870012 &&
      (recipient !== initialRecipient ||
        submissionMethod !== initialSubmissionMethod ||
        facilitiesChanged));

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const doSave = async (reviewerInputs?: SubmissionReviewerInput[]) => {
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            // Only the document flow edits the title; the 8700-12 keeps its
            // derived, read-only title untouched.
            ...(allowTitleEdit && { title: title.trim() }),
            dueDate: date ? (toUtcMidnightIsoFromDateLike(date) ?? undefined) : undefined,
            // Encamp Assignee is a staff-only field; customers can't edit it, so
            // only staff include it (the backend rejects it from customers).
            ...(isStaff && { staffAssigneeId: assignee?.id ?? null }),
            notes,
            // §1 is an 8700-12 form field; document forms have no equivalent.
            ...(isRcra870012 && { reasonForSubmittal: reasonForSubmittal || null }),
            // Recipient/method/facility are document-form-only edits.
            ...(!isRcra870012 && {
              recipient,
              submissionMethod: submissionMethod || undefined,
              ...(facilitiesChanged && { facilityIds }),
            }),
            ...(reviewerInputs ? { reviewers: reviewerInputs } : {}),
          },
        },
      });
      alerts.success("Saved.");
      onClose();
    } catch (error) {
      alerts.error("Failed to save submission details.", error);
    }
  };

  const handlePrimary = () => {
    // Diff in whole calendar days, not milliseconds: both dates are local
    // midnight, so a DST transition inside one range and not the other makes a
    // millisecond delta ±1h off a whole day — enough to land the reviewer date
    // at 23:00 the previous day and persist it a day early.
    const deltaDays = date && initialDate ? Math.round(date.diff(initialDate, "days").days) : 0;
    const shiftable = (dueDateShift?.reviewers ?? []).filter((r) => r.dueDate?.isValid);
    // Only offer the shift when the due date actually moved and there are
    // reviewer dates to move; otherwise save straight through.
    if (dueDateShift && deltaDays !== 0 && shiftable.length > 0) {
      setPendingShiftDeltaDays(deltaDays);
      return;
    }
    void doSave();
  };

  // Encamp Assignee (flexes to fill) + Due date (fixed compact width) on one
  // row, matching the Details card's read-only layout.
  const rowSx = {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
    alignItems: "flex-start",
  } as const;

  return (
    <>
      <Dialog
        open
        title="Edit Submission Details"
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          // Recipient/method/facility are only required to send to reviewers, so
          // a legacy submission missing them can still edit its other details.
          disabled: !isDirty || !date || (allowTitleEdit && !title.trim()),
          onClick: handlePrimary,
        }}
      >
        {allowTitleEdit && (
          <TextField
            size="small"
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
            fullWidth
          />
        )}
        {!isRcra870012 && (
          <DocumentFormFieldsSection
            facilityIds={facilityIds}
            canEditFacilities={canEditFacilities}
            onChangeFacility={() => setChangeFacilityOpen(true)}
            onRemoveFacility={(facilityId) =>
              setFacilityIds((ids) => ids.filter((id) => id !== facilityId))
            }
            recipient={recipient}
            onRecipientChange={setRecipient}
            submissionMethod={submissionMethod}
            onSubmissionMethodChange={setSubmissionMethod}
            rowSx={rowSx}
          />
        )}
        <Box sx={rowSx}>
          {isStaff && (
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <ReviewerPicker
                staffOnly
                value={assignee ? { ...assignee, order: 0, isStaff: true } : null}
                onChange={(reviewer) =>
                  setAssignee(
                    reviewer
                      ? {
                          id: reviewer.id,
                          name: reviewer.name,
                          email: reviewer.email,
                        }
                      : null
                  )
                }
                inputLabelProps={{ label: "Encamp Assignee", size: "small" }}
              />
            </Box>
          )}
          <DatePicker
            label="Due date"
            value={date}
            onChange={setDate}
            slotProps={{
              textField: { size: "small", sx: { width: 220 }, required: true },
            }}
          />
        </Box>
        {isRcra870012 && (
          <TextField
            select
            size="small"
            label="Reason for Submittal"
            value={reasonForSubmittal}
            onChange={(e) =>
              setReasonForSubmittal(isReasonForSubmittal(e.target.value) ? e.target.value : "")
            }
            helperText="Appears as Reason for Submittal (§1) on the 8700-12 form."
          >
            {reasonForSubmittalOptionsForSite(!isNewSite).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          multiline
          minRows={3}
          size="small"
          label={isRcra870012 ? "Public Comments" : "Notes"}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText={
            isRcra870012 ? "Appears as Public Comments (§18) on the 8700-12 form." : undefined
          }
        />
        {isRcra870012 && isNewSite && (
          <Alert severity="info">
            This facility has no EPA ID. Submitting this 8700-12 registers a new site and requests a
            new EPA ID, which must be approved by the EPA or your authorized state agency.
          </Alert>
        )}
      </Dialog>
      {changeFacilityOpen && (
        <AssociateFacilitiesDialog
          open
          onClose={() => setChangeFacilityOpen(false)}
          existingFacilityIds={facilityIds}
          subtitle="Choose the facilities this submission covers."
          onSave={(selectedIds) => {
            setFacilityIds(selectedIds);
            setChangeFacilityOpen(false);
          }}
        />
      )}
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
      {dueDateShift && pendingShiftDeltaDays !== null && (
        <ReviewerDueDateShiftDialog
          open
          deltaDays={pendingShiftDeltaDays}
          reviewers={dueDateShift.reviewers}
          loading={saving}
          onConfirm={() => {
            const delta = pendingShiftDeltaDays;
            dueDateShift.onConfirm(delta);
            setPendingShiftDeltaDays(null);
            void doSave(dueDateShift.buildReviewerInputs(delta));
          }}
          // "Keep as-is" is the explicit choice to save the new due date without
          // moving reviewer dates.
          onCancel={() => {
            setPendingShiftDeltaDays(null);
            void doSave();
          }}
          // Escape, the backdrop, and the X are dismissals, not decisions — drop
          // back to the edit dialog with nothing saved.
          onClose={() => setPendingShiftDeltaDays(null)}
        />
      )}
    </>
  );
}
